import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Flex,
  Text,
  Tag,
  Switch,
  Divider,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  scraperSchedulesMonthly,
  scraperScheduleCreate,
  scraperScheduleUpdateHashtags,
  scraperScheduleUpdateTime,
  scraperScheduleToggle,
  scraperScheduleDelete,
} from "../../api";

const now = new Date();

/** UTC 문자열을 KST로 포맷 */
const formatUtcToKst = (utcStr) => {
  if (!utcStr) return "-";
  const iso = typeof utcStr === "string" && !/Z|[+-]\d{2}:?\d{2}$/.test(utcStr)
    ? `${utcStr}Z`
    : utcStr;
  return new Date(iso).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
};

export function ScheduleManageModal({ isOpen, onClose, onSuccess }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const toast = useToast();

  const normalizeSchedule = (s) => ({
    id: s.id,
    scheduleName: s.scheduleName ?? s.schedule_name,
    hashtags: s.hashtags ?? [],
    executionTime: s.executionTime ?? s.execution_time ?? "00:00",
    resultsLimit: s.resultsLimit ?? s.results_limit ?? 100,
    isActive: s.isActive ?? s.is_active ?? s.active ?? false,
    lastRunAt: s.lastRunAt ?? s.last_run_at,
  });

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const y = now.getFullYear();
      const m = now.getMonth() + 1;
      const res = await scraperSchedulesMonthly({ year: y, month: m });
      const raw = res?.data?.schedules ?? [];
      setSchedules(raw.map(normalizeSchedule));
    } catch (e) {
      console.error("스케줄 조회 실패:", e);
      toast({ title: "스케줄 조회 실패", status: "error", duration: 2000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleToggle = async (id) => {
    try {
      const res = await scraperScheduleToggle(id);
      const newActive = res?.data?.is_active ?? res?.data?.active;
      if (typeof newActive === "boolean") {
        setSchedules((prev) =>
          prev.map((s) =>
            s.id === id ? { ...s, isActive: newActive } : s
          )
        );
      } else {
        loadSchedules();
      }
      toast({ title: "상태 변경됨", status: "success", duration: 2000 });
      onSuccess?.();
    } catch (e) {
      toast({
        title: "변경 실패",
        description: e?.response?.data?.message,
        status: "error",
        duration: 2000,
      });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" 스케줄을 삭제하시겠습니까?`)) return;
    try {
      await scraperScheduleDelete(id);
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      setEditSchedule((prev) => (prev?.id === id ? null : prev));
      toast({ title: "삭제됨", status: "success", duration: 2000 });
      onSuccess?.();
    } catch (e) {
      toast({
        title: "삭제 실패",
        description: e?.response?.data?.message,
        status: "error",
        duration: 2000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>스케줄 관리</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" pb="24px">
          <Flex justify="flex-end" mb="16px">
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => setIsAddOpen(true)}
            >
              새 스케줄 추가
            </Button>
          </Flex>

          {loading ? (
            <Flex justify="center" py="40px">
              <Spinner size="lg" />
            </Flex>
          ) : schedules.length === 0 ? (
            <Text color="gray.500" py="40px" textAlign="center">
              등록된 스케줄이 없습니다.
            </Text>
          ) : (
            <Box>
              {schedules.map((s) => (
                <Box key={s.id}>
                  <Flex
                    align="center"
                    justify="space-between"
                    py="12px"
                    gap="16px"
                    flexWrap="wrap"
                  >
                    <Box flex="1" minW="140px">
                      <Text fontWeight="600">{s.scheduleName}</Text>
                      <Flex gap="4px" mt="4px" flexWrap="wrap">
                        {s.hashtags?.map((h, i) => (
                          <Tag key={i} size="sm">
                            #{h}
                          </Tag>
                        ))}
                      </Flex>
                      <Text fontSize="xs" color="gray.400" mt="4px">
                        마지막 수집 시각: {formatUtcToKst(s.lastRunAt)}
                      </Text>
                    </Box>
                    <Text fontSize="sm" color="gray.600">
                      {s.executionTime} / limit {s.resultsLimit ?? 100}
                    </Text>
                    <Flex align="center" gap="8px">
                      <Text fontSize="sm" color="gray.500">
                        {s.isActive ? "활성" : "비활성"}
                      </Text>
                      <Switch
                        isChecked={s.isActive}
                        onChange={() => handleToggle(s.id)}
                        colorScheme="blue"
                      />
                    </Flex>
                    <Flex gap="8px">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setEditSchedule(editSchedule?.id === s.id ? null : s)
                        }
                      >
                        {editSchedule?.id === s.id ? "취소" : "수정"}
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleDelete(s.id, s.scheduleName)}
                      >
                        삭제
                      </Button>
                    </Flex>
                  </Flex>
                  {editSchedule?.id === s.id && (
                    <ScheduleEditForm
                      schedule={s}
                      onSuccess={() => {
                        setEditSchedule(null);
                        loadSchedules();
                        onSuccess?.();
                      }}
                      onCancel={() => setEditSchedule(null)}
                      toast={toast}
                    />
                  )}
                  <Divider />
                </Box>
              ))}
            </Box>
          )}

          {isAddOpen && (
            <ScheduleAddForm
              onSuccess={() => {
                setIsAddOpen(false);
                loadSchedules();
                onSuccess?.();
              }}
              onCancel={() => setIsAddOpen(false)}
              toast={toast}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function ScheduleEditForm({ schedule, onSuccess, onCancel, toast }) {
  const [hashtagsStr, setHashtagsStr] = useState(
    schedule.hashtags?.join(", ") ?? ""
  );
  const [executionTime, setExecutionTime] = useState(
    schedule.executionTime ?? "00:00"
  );
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    const tags = hashtagsStr
      .split(/[\s,]+/)
      .map((t) => t.replace(/^#/, "").trim())
      .filter(Boolean);
    if (tags.length === 0) {
      toast({ title: "해시태그를 입력하세요", status: "warning", duration: 2000 });
      return;
    }
    setLoading(true);
    try {
      await scraperScheduleUpdateHashtags(schedule.id, tags);
      await scraperScheduleUpdateTime(schedule.id, executionTime);
      toast({ title: "저장됨", status: "success", duration: 2000 });
      onSuccess?.();
    } catch (e) {
      toast({
        title: "저장 실패",
        description: e?.response?.data?.message,
        status: "error",
        duration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      p="16px"
      bg="gray.50"
      borderRadius="8px"
      mt="8px"
      mb="12px"
    >
      <FormControl mb="12px">
        <FormLabel fontSize="sm">해시태그 (쉼표로 구분)</FormLabel>
        <Input
          value={hashtagsStr}
          onChange={(e) => setHashtagsStr(e.target.value)}
          placeholder="광고, 협찬, sponsored"
        />
      </FormControl>
      <FormControl mb="12px">
        <FormLabel fontSize="sm">실행 시간</FormLabel>
        <Input
          type="time"
          value={executionTime}
          onChange={(e) => setExecutionTime(e.target.value)}
          w="160px"
          minW="160px"
          pr="8px"
        />
      </FormControl>
      <Flex gap="8px">
        <Button size="sm" colorScheme="blue" onClick={handleSave} isLoading={loading}>
          저장
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          취소
        </Button>
      </Flex>
    </Box>
  );
}

function ScheduleAddForm({ onSuccess, onCancel, toast }) {
  const [scheduleName, setScheduleName] = useState("");
  const [hashtagsStr, setHashtagsStr] = useState("");
  const [executionTime, setExecutionTime] = useState("02:00");
  const [resultsLimit, setResultsLimit] = useState(500);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    const tags = hashtagsStr
      .split(/[\s,]+/)
      .map((t) => t.replace(/^#/, "").trim())
      .filter(Boolean);
    if (!scheduleName.trim()) {
      toast({ title: "스케줄 이름을 입력하세요", status: "warning", duration: 2000 });
      return;
    }
    if (tags.length === 0) {
      toast({ title: "해시태그를 입력하세요", status: "warning", duration: 2000 });
      return;
    }
    setLoading(true);
    try {
      await scraperScheduleCreate({
        scheduleName: scheduleName.trim(),
        hashtags: tags,
        executionTime,
        resultsLimit,
      });
      toast({ title: "생성됨", status: "success", duration: 2000 });
      onSuccess?.();
    } catch (e) {
      toast({
        title: "생성 실패",
        description: e?.response?.data?.message ?? e?.message,
        status: "error",
        duration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p="16px" bg="blue.50" borderRadius="8px" mb="16px">
      <FormControl mb="12px">
        <FormLabel fontSize="sm">스케줄 이름</FormLabel>
        <Input
          value={scheduleName}
          onChange={(e) => setScheduleName(e.target.value)}
          placeholder="예: 협찬·브랜드 관련"
        />
      </FormControl>
      <FormControl mb="12px">
        <FormLabel fontSize="sm">해시태그 (쉼표로 구분, 1~20개)</FormLabel>
        <Input
          value={hashtagsStr}
          onChange={(e) => setHashtagsStr(e.target.value)}
          placeholder="광고, 협찬, sponsored, collab"
        />
      </FormControl>
      <Flex gap="16px" mb="12px">
        <FormControl w="160px" minW="160px">
          <FormLabel fontSize="sm">실행 시간</FormLabel>
          <Input
            type="time"
            value={executionTime}
            onChange={(e) => setExecutionTime(e.target.value)}
            pr="8px"
          />
        </FormControl>
        <FormControl w="120px">
          <FormLabel fontSize="sm">결과 제한</FormLabel>
          <Input
            type="number"
            value={resultsLimit}
            onChange={(e) => setResultsLimit(Number(e.target.value))}
            min={1}
          />
        </FormControl>
      </Flex>
      <Flex gap="8px">
        <Button size="sm" colorScheme="blue" onClick={handleCreate} isLoading={loading}>
          추가
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          취소
        </Button>
      </Flex>
    </Box>
  );
}
